import React, { useEffect, useState } from "react";
import { AiOutlineWarning } from "react-icons/ai";
import { BiGitMerge } from "react-icons/bi";
import { FiCheckCircle } from "react-icons/fi";
import Button from "../Button";
import Modal from "../Modal";
import { useGetAllBatches } from "../../apis/batches/get-all-batches";
import { usePreviewBatchMerge } from "../../apis/batches/preview-batch-merge";
import { useMergeBatches } from "../../apis/batches/merge-batches";

export interface IMergeBatchSource {
    _id: string;
    batchNumber?: string;
    totalOrders?: number;
    totalPickUpStops?: number;
    totalDropOffStops?: number;
    type?: string;
    status?: string;
}

interface MergeBatchModalProps {
    isVisible: boolean;
    onHide: () => void;
    sourceBatch: IMergeBatchSource | null;
    onSuccess?: (mergedTargetBatchId: string) => void;
}

const MergeBatchModal = ({
    isVisible,
    onHide,
    sourceBatch,
    onSuccess,
}: MergeBatchModalProps) => {
    const [selectedTargetBatchId, setSelectedTargetBatchId] = useState<string>("");

    // Fetch all active forming batches
    const { data: batchesData, isFetching: isLoadingBatches } = useGetAllBatches({
        status: "forming",
        limit: 100,
    });

    const candidateBatches = (batchesData?.docs || []).filter(
        (b) => b._id !== sourceBatch?._id
    );

    const selectedTargetBatch = candidateBatches.find(
        (b) => b._id === selectedTargetBatchId
    );

    const {
        mutate: runPreview,
        data: previewResult,
        isLoading: isCheckingPreview,
        isError: isPreviewError,
        error: previewError,
        reset: resetPreview,
    } = usePreviewBatchMerge();

    const {
        mutateAsync: executeMerge,
        isLoading: isMerging,
    } = useMergeBatches();

    // Reset target batch and preview on open/close or source change
    useEffect(() => {
        if (isVisible) {
            setSelectedTargetBatchId("");
            resetPreview();
        } else {
            setSelectedTargetBatchId("");
            resetPreview();
        }
    }, [isVisible, sourceBatch?._id]);

    // Automatically trigger pre-flight preview when target batch is selected
    const handleTargetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const targetId = e.target.value;
        setSelectedTargetBatchId(targetId);

        if (targetId && sourceBatch?._id) {
            runPreview({
                sourceBatchId: sourceBatch._id,
                targetBatchId: targetId,
            });
        } else {
            resetPreview();
        }
    };

    const handleConfirmMerge = async () => {
        if (!sourceBatch?._id || !selectedTargetBatchId) return;

        try {
            await executeMerge({
                sourceBatchId: sourceBatch._id,
                targetBatchId: selectedTargetBatchId,
            });
            onHide();
            if (onSuccess) {
                onSuccess(selectedTargetBatchId);
            }
        } catch (err) {
            // Error toast handled in mutation onError
        }
    };

    const isMergeDisabled =
        !selectedTargetBatchId ||
        isCheckingPreview ||
        !previewResult?.eligible ||
        isMerging;

    return (
        <Modal
            isVisible={isVisible}
            onHide={onHide}
            heading="Merge Batches"
            containerClassName="max-w-2xl lg:max-w-3xl"
        >
            <div className="px-6 py-4 flex flex-col gap-5 text-sm font-inter">
                {/* Source Batch Card */}
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">
                        Source Batch (Orders will be moved from here)
                    </label>
                    <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-base text-primary">
                                    #{sourceBatch?.batchNumber || sourceBatch?._id?.slice(-8) || "N/A"}
                                </span>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 uppercase tracking-wider">
                                    Forming
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 font-mono mt-1 break-all">
                                ID: {sourceBatch?._id}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-gray-600">
                            <span className="bg-white px-2.5 py-1 rounded-md border border-gray-200 shadow-2xs">
                                Orders: <strong className="text-gray-900">{sourceBatch?.totalOrders ?? 0}</strong>
                            </span>
                            <span className="bg-white px-2.5 py-1 rounded-md border border-gray-200 shadow-2xs">
                                Pickups: <strong className="text-gray-900">{sourceBatch?.totalPickUpStops ?? 0}</strong>
                            </span>
                            <span className="bg-white px-2.5 py-1 rounded-md border border-gray-200 shadow-2xs">
                                Dropoffs: <strong className="text-gray-900">{sourceBatch?.totalDropOffStops ?? 0}</strong>
                            </span>
                        </div>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1 italic">
                        Note: Once merged, this source batch will be cancelled and closed.
                    </p>
                </div>

                {/* Target Batch Selection */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Target Batch (Receives merged orders) *
                        </label>
                        {isLoadingBatches && (
                            <span className="text-xs text-primary flex items-center gap-1 font-medium">
                                <span className="loading loading-spinner loading-xs text-primary" /> Loading batches...
                            </span>
                        )}
                    </div>

                    {candidateBatches.length === 0 && !isLoadingBatches ? (
                        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2.5">
                            <AiOutlineWarning className="text-base text-amber-600 shrink-0 mt-0.5" />
                            <div>
                                <strong className="block mb-0.5">No other forming batches available</strong>
                                There are currently no other open forming batches to merge with. Merging requires at least two active batches in forming status.
                            </div>
                        </div>
                    ) : (
                        <select
                            value={selectedTargetBatchId}
                            onChange={handleTargetChange}
                            disabled={isLoadingBatches || isMerging}
                            className="select select-bordered select-primary w-full text-sm font-normal bg-white h-11 min-h-0 focus:outline-none"
                        >
                            <option value="">-- Select target forming batch --</option>
                            {candidateBatches.map((batch) => (
                                <option key={batch._id} value={batch._id}>
                                    Batch #{batch.batchNumber || batch._id.slice(-6)} ({batch.totalOrders ?? 0} orders · {batch.totalPickUpStops ?? 0} pickups · {batch.totalDropOffStops ?? 0} dropoffs)
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Pre-flight Preview Section */}
                {selectedTargetBatchId && (
                    <div className="flex flex-col gap-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Pre-flight Validation & Projected Metrics
                        </label>

                        {isCheckingPreview ? (
                            <div className="p-5 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center gap-3 text-center">
                                <span className="loading loading-spinner text-primary" />
                                <span className="text-xs text-gray-600 font-medium">
                                    Running pre-flight checks (stop limits, pickup consolidation, schedule compatibility)...
                                </span>
                            </div>
                        ) : previewResult?.eligible ? (
                            <div className="flex flex-col gap-3">
                                {/* Success Badge */}
                                <div className="p-3.5 rounded-xl bg-green-50 border border-green-200 text-xs text-green-800 flex items-start gap-2.5">
                                    <FiCheckCircle className="text-base text-green-600 shrink-0 mt-0.5" />
                                    <div>
                                        <strong className="block font-semibold">Eligible to merge</strong>
                                        Pre-flight validation passed. Stop limits and schedule constraints are satisfied.
                                    </div>
                                </div>

                                {/* Projected Summary Grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 flex flex-col">
                                        <span className="text-xs text-gray-500 font-medium">Projected Orders</span>
                                        <span className="text-base font-bold text-gray-900 mt-1">
                                            {previewResult.preview?.projectedTotalOrders ?? "—"}
                                        </span>
                                        <span className="text-[11px] text-gray-400 mt-0.5">
                                            {(sourceBatch?.totalOrders || 0)} + {(selectedTargetBatch?.totalOrders || 0)}
                                        </span>
                                    </div>

                                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 flex flex-col">
                                        <span className="text-xs text-gray-500 font-medium">Pickup Stops</span>
                                        <span className="text-base font-bold text-gray-900 mt-1">
                                            {previewResult.preview?.projectedPickupStops ?? "—"} <span className="text-xs font-normal text-gray-400">/ 4 max</span>
                                        </span>
                                        <span className="text-[11px] text-gray-400 mt-0.5">
                                            Consolidated ≤ 0.1 mi
                                        </span>
                                    </div>

                                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 flex flex-col">
                                        <span className="text-xs text-gray-500 font-medium">Dropoff Stops</span>
                                        <span className="text-base font-bold text-gray-900 mt-1">
                                            {previewResult.preview?.projectedDropoffStops ?? "—"} <span className="text-xs font-normal text-gray-400">/ 4 max</span>
                                        </span>
                                        <span className="text-[11px] text-gray-400 mt-0.5">
                                            Delivery stops
                                        </span>
                                    </div>

                                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 flex flex-col">
                                        <span className="text-xs text-gray-500 font-medium">Route Distance</span>
                                        <span className="text-base font-bold text-gray-900 mt-1">
                                            {previewResult.preview?.projectedTotalDistance
                                                ? `${previewResult.preview.projectedTotalDistance.toFixed(1)} mi`
                                                : "—"}
                                        </span>
                                    </div>
                                </div>

                                {/* Projected Type and Info */}
                                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-500">Projected Batch Type:</span>
                                        <span className="font-semibold uppercase tracking-wider text-[11px] bg-primary/10 text-primary border border-primary/20 px-2.5 py-0.5 rounded-full">
                                            {previewResult.preview?.projectedType?.replace(/_/g, " ") || "Batch"}
                                        </span>
                                    </div>
                                    <span className="text-[11px] text-gray-500">
                                        Real-time pricing split recalculation enabled
                                    </span>
                                </div>
                            </div>
                        ) : previewResult && !previewResult.eligible ? (
                            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-800 flex items-start gap-2.5">
                                <AiOutlineWarning className="text-base text-red-600 shrink-0 mt-0.5" />
                                <div>
                                    <strong className="block font-semibold mb-0.5">Merge Ineligible</strong>
                                    {previewResult.reason || "The selected batches cannot be merged because they exceed configuration constraints."}
                                </div>
                            </div>
                        ) : isPreviewError ? (
                            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-800 flex items-start gap-2.5">
                                <AiOutlineWarning className="text-base text-red-600 shrink-0 mt-0.5" />
                                <div>
                                    <strong className="block font-semibold mb-0.5">Validation Error</strong>
                                    {(previewError as any)?.response?.data?.message || (previewError as any)?.message || "Failed to validate batch merge."}
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}

                {/* Footer Buttons */}
                <div className="flex flex-col-reverse sm:flex-row gap-3 items-center justify-center pt-3 border-t border-gray-100">
                    <Button
                        type="button"
                        variant="solid"
                        disabled={isMergeDisabled}
                        isLoading={isMerging}
                        onClick={handleConfirmMerge}
                        className="w-full sm:w-44 min-h-0 h-10 font-semibold"
                    >
                        <BiGitMerge className="text-lg" />
                        <span>Confirm & Merge</span>
                    </Button>
                    <Button
                        type="button"
                        disabled={isMerging}
                        onClick={onHide}
                        className="w-full sm:w-32 min-h-0 h-10 bg-gray-200 text-black border-none md:hover:text-black md:hover:bg-gray-200"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default MergeBatchModal;
